import { defaultKeybindings } from '../model/defaultKeybindings';
import type {
  KeybindingCommandId,
  KeybindingScope,
} from '../model/keybindingTypes';
import type { ResolvedKeybindingTarget } from './targetRegistry';

export type AppCommandSource =
  | 'keyboard'
  | 'menu-click'
  | 'menu-accelerator'
  | 'palette';

export type CommandGateDocument = {
  querySelectorAll: (selector: string) => ArrayLike<Element>;
};

export type CommandGateEnvironment = {
  readonly documentObject?: CommandGateDocument;
  readonly isFeatureEnabled?: (commandId: KeybindingCommandId, source: AppCommandSource) => boolean;
};

const OPEN_DIALOG_SELECTOR = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
].join(',');

function definitionFor(id: string) {
  const entries = Object.entries(defaultKeybindings) as Array<[
    KeybindingCommandId,
    (typeof defaultKeybindings)[KeybindingCommandId],
  ]>;
  return entries.find(([commandId, definition]) => commandId === id ||
    definition.menuCommand === id || definition.paletteCommandId === id);
}

function commandScope(id: string, target: ResolvedKeybindingTarget | null): KeybindingScope {
  return target?.scope ?? definitionFor(id)?.[1].scope ?? 'global';
}

function topmostOpenDialog(documentObject: CommandGateDocument | undefined): Element | null {
  if (!documentObject) return null;
  const dialogs = Array.from(documentObject.querySelectorAll(OPEN_DIALOG_SELECTOR))
    .filter((dialog) => !dialog.closest('[data-radix-popper-content-wrapper]'));
  return dialogs[dialogs.length - 1] ?? null;
}

export function keybindingCommandIdFor(id: string): KeybindingCommandId | null {
  return definitionFor(id)?.[0] ?? null;
}

export function isCommandAllowed(
  id: string,
  source: AppCommandSource,
  target: ResolvedKeybindingTarget | null,
  environment: CommandGateEnvironment = {},
): boolean {
  const commandId = keybindingCommandIdFor(id);
  if (commandId && environment.isFeatureEnabled?.(commandId, source) === false) return false;
  if (commandId && target?.canExecute(commandId) === false) return false;

  const documentObject = environment.documentObject ?? globalThis.document;
  const dialog = topmostOpenDialog(documentObject);
  if (!dialog || commandScope(id, target) === 'global') return true;
  if (!commandId || !target) return false;

  const targetElement = target.getRootElement(commandId);
  return Boolean(targetElement && (dialog === targetElement || dialog.contains(targetElement)));
}
